import { Box, BoxProps, ButtonProps, useToken } from "@chakra-ui/react";
import React, { MutableRefObject, ReactElement, createContext, useRef } from "react";

import { TableBody } from "./components/table-body";
import { TableCell } from "./components/table-cell";
import { TableContainer } from "./components/table-container";
import { TableEmpty } from "./components/table-empty";
import { RanaSearch, TableFilters } from "./components/table-filters";
import { TableHeader } from "./components/table-header";
import { TablePagination } from "./components/table-pagination";
import { TABLE_ROW_HEIGHT, TableRow } from "./components/table-row";
import { useTableCheckboxes } from "./hooks/use-table-checkboxes";

export const TABLE_SELECTION_COLUMN_WIDTH = "24px";
export const TABLE_ACCORDION_COLUMN_WIDTH = "24px";

export type ColumnOrdering = "az" | "za" | undefined;

export interface ColumnDefinition {
  width: string;
  title?: string;
  titleRenderFunction?: () => string | React.ReactNode;
  orderingField?: string;
  align?: "left" | "right" | "center"; // Default left
}

export interface TableAction extends ButtonProps {
  label: string;
  action: (rowIds: string[]) => void;
  icon?: ReactElement;
  getDisabledText?: (rowIds: string[]) => string | undefined;
}

export interface TableProps extends BoxProps {
  columnDefinitions: ColumnDefinition[];
  rows?: number;
  tableDataIds: string[];
  defaultSelected?: string[];
  isLoading?: boolean;
  isSkeleton?: boolean;
  tableActions?: TableAction[];
  onCheckboxClicked?: (rowIds: string[]) => void;

  ordering?: { [fieldName: string]: ColumnOrdering };
  setOrdering?: (fieldName: string, ordering: ColumnOrdering) => void;

  hasRowActions?: boolean; // Table needs to know this to calculate the width of the row actions column
  hasAccordion?: boolean; // Table needs to know this to calculate the width of the chevron column

  spacing?: string;
  lineHeight?: string;
  themeColor?: string;

  emptyElement: JSX.Element;
}

interface TableContext {
  isLoading: boolean;
  isSkeleton: boolean;
  isEmpty: boolean;
  rowChecked: (rowId: string) => boolean;
  selectOnly: (rowId: string) => void;
  setChecked: (rowId: string, event: React.MouseEvent<HTMLElement>) => void;
  checkedRows: string[];
  allChecked: boolean;
  someChecked: boolean;
  deselectAll: () => void;
  setAllChecked: (event: React.ChangeEvent<HTMLInputElement>) => void;
  hasSelection: boolean;
  hasRowActions: boolean;
  hasAccordion: boolean;
  columnDefinitions: ColumnDefinition[];
  ordering?: { [fieldName: string]: ColumnOrdering };
  setOrdering?: (fieldName: string, ordering: ColumnOrdering) => void;
  templateColumns: string;
  spacing: string;
  emptyElement: React.ReactNode;
  tableActions: TableAction[];
  onCheckboxClicked?: (rowIds: string[]) => void;
  containerRef: MutableRefObject<HTMLDivElement | null>;
}

export const TableContext = createContext<TableContext>({
  isLoading: false,
  isSkeleton: false,
  isEmpty: false,
  rowChecked: () => false,
  selectOnly: () => undefined,
  setChecked: () => undefined,
  checkedRows: [],
  allChecked: false,
  someChecked: false,
  deselectAll: () => undefined,
  setAllChecked: () => undefined,
  hasSelection: false,
  hasRowActions: false,
  hasAccordion: false,
  columnDefinitions: [],
  templateColumns: "",
  spacing: "0",
  emptyElement: "No data to display",
  tableActions: [],
  containerRef: { current: null },
});

export function RanaTable(props: TableProps) {
  const {
    columnDefinitions,
    isLoading = false,
    isSkeleton = false,
    tableDataIds,
    width,
    tableActions = [],
    defaultSelected,
    ordering,
    setOrdering,
    spacing = "1rem",
    emptyElement,
    hasRowActions = false,
    hasAccordion = false,
    children,
    onCheckboxClicked,
    ...rest
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const hasSelection = Boolean(tableActions && tableActions.length > 0);

  const columnWidths: string[] = [
    ...(hasAccordion ? [TABLE_ACCORDION_COLUMN_WIDTH] : []),
    ...(hasSelection ? [TABLE_SELECTION_COLUMN_WIDTH] : []),
    ...columnDefinitions.map(({ width }) => width),
    ...(hasRowActions ? [TABLE_ROW_HEIGHT] : []),
  ];

  const isEmpty = tableDataIds.length === 0;

  const templateColumns = useToken("sizes", columnWidths)
    // Makes sure last column item automatically fills in leftover space but keeps width as minimum
    .map((width, idx) => (idx === columnWidths.length - 1 ? `minmax(${width}, 1fr)` : width))
    .join(" ");

  const checkboxes = useTableCheckboxes(tableDataIds, defaultSelected);

  // Only use checkbox values when hasSelection is true
  const emptyCheckboxState = {
    rowChecked: () => false,
    selectOnly: () => undefined,
    setChecked: () => undefined,
    checkedRows: [],
    allChecked: false,
    someChecked: false,
    deselectAll: () => undefined,
    setAllChecked: () => undefined,
  };

  const {
    rowChecked,
    selectOnly,
    setChecked,
    checkedRows,
    allChecked,
    someChecked,
    deselectAll,
    setAllChecked,
  } = hasSelection ? checkboxes : emptyCheckboxState;

  return (
    <Box h="full" w="full" {...rest}>
      <TableContext.Provider
        value={{
          isEmpty,
          isLoading,
          isSkeleton,
          rowChecked,
          selectOnly,
          setChecked,
          checkedRows,
          allChecked,
          someChecked,
          deselectAll,
          setAllChecked,
          columnDefinitions,
          ordering,
          setOrdering,
          hasRowActions,
          hasAccordion,
          templateColumns,
          spacing,
          hasSelection,
          tableActions,
          emptyElement: emptyElement ?? "No data to display",
          onCheckboxClicked,
          containerRef,
        }}
      >
        {children}
      </TableContext.Provider>
    </Box>
  );
}

// Export internal components as well
RanaTable.Empty = TableEmpty;
RanaTable.Filters = TableFilters;
RanaTable.Header = TableHeader;
RanaTable.Row = TableRow;
RanaTable.Cell = TableCell;
RanaTable.Body = TableBody;
RanaTable.Pagination = TablePagination;
RanaTable.Container = TableContainer;
RanaTable.FilterComponents = {
  Search: RanaSearch,
};
